import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Calendar,
  MessageSquare,
  Bot,
  CreditCard,
  Activity,
  TrendingUp,
  Clock,
  Users,
  Eye,
  Search,
  X,
  Link2,
  Globe,
  Webhook
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ConversationDetailModal } from '@/components/builder/ConversationDetailModal';
import { GlobalIntegrations } from '@/components/portal/GlobalIntegrations';

interface UserActivitySectionProps {
  userId: string;
}

interface ActivityItem {
  id: string;
  type: 'conversation' | 'chatbot' | 'billing' | 'login';
  title: string;
  description: string;
  timestamp: string;
  metadata?: any;
}

export const UserActivitySection: React.FC<UserActivitySectionProps> = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [conversationSearchTerm, setConversationSearchTerm] = useState('');
  const [pingPostProviders, setPingPostProviders] = useState<any[]>([]);
  const [isLoadingPingPost, setIsLoadingPingPost] = useState(false);

  // Fetch user activity data
  const { data: conversations = [], isLoading: conversationsLoading } = useQuery({
    queryKey: ['userConversations', userId],
    queryFn: async () => {
      // First get user's chatbots, then get conversations for those chatbots
      const { data: userChatbots, error: chatbotsError } = await supabase
        .from('chatbots')
        .select('id')
        .eq('user_id', userId);

      if (chatbotsError) throw chatbotsError;

      if (!userChatbots || userChatbots.length === 0) {
        return []; // No chatbots, no conversations
      }

      const chatbotIds = userChatbots.map(bot => bot.id);

      const { data, error } = await supabase
        .from('conversations')
        .select(`
          id,
          created_at,
          updated_at,
          chatbot_id,
          chatbot:chatbots(name)
        `)
        .in('chatbot_id', chatbotIds)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Get message counts for each conversation
      const conversationsWithCounts = await Promise.all(
        data.map(async (conv) => {
          const { count, error: countError } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id);

          if (countError) {
            console.error('Error counting messages for conversation:', conv.id, countError);
            return { ...conv, message_count: 0 };
          }

          return { ...conv, message_count: count || 0 };
        })
      );

      return conversationsWithCounts;
    },
  });

  const { data: chatbots = [], isLoading: chatbotsLoading } = useQuery({
    queryKey: ['userChatbots', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chatbots')
        .select('id, name, created_at, active')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Calculate activity stats
  const activityStats = {
    totalConversations: conversations.length,
    totalChatbots: chatbots.length,
    activeChatbots: chatbots.filter(bot => bot.active).length,
    totalMessages: conversations.reduce((total, conv) => total + (conv.message_count || 0), 0),
  };

  // Generate recent activity timeline
  const recentActivity: ActivityItem[] = [
    ...conversations.slice(0, 5).map(conv => ({
      id: conv.id,
      type: 'conversation' as const,
      title: 'New Conversation',
      description: `Started conversation with ${conv.chatbot?.name || 'Unknown Bot'}`,
      timestamp: conv.created_at,
      metadata: { chatbot: conv.chatbot?.name }
    })),
    ...chatbots.slice(0, 3).map(bot => ({
      id: bot.id,
      type: 'chatbot' as const,
      title: 'Chatbot Created',
      description: `Created chatbot "${bot.name}"`,
      timestamp: bot.created_at,
      metadata: { name: bot.name, active: bot.active }
    }))
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 10);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'conversation':
        return <MessageSquare className="h-4 w-4 text-blue-600" />;
      case 'chatbot':
        return <Bot className="h-4 w-4 text-green-600" />;
      case 'billing':
        return <CreditCard className="h-4 w-4 text-purple-600" />;
      case 'login':
        return <Users className="h-4 w-4 text-orange-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityBadge = (type: string) => {
    const colors = {
      conversation: 'bg-blue-100 text-blue-800',
      chatbot: 'bg-green-100 text-green-800',
      billing: 'bg-purple-100 text-purple-800',
      login: 'bg-orange-100 text-orange-800',
    };

    return (
      <Badge className={colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800'}>
        {type}
      </Badge>
    );
  };

  const handleViewConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsConversationModalOpen(true);
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setSelectedConversationId(null);
  };

  // Filter conversations based on search term
  const filteredConversations = conversations.filter((conversation) => {
    if (!conversationSearchTerm) return true;

    const searchLower = conversationSearchTerm.toLowerCase();
    const chatbotName = conversation.chatbot?.name?.toLowerCase() || '';
    const conversationId = conversation.id.toLowerCase();
    const messageCount = conversation.message_count?.toString() || '0';

    // Format dates for searching
    const createdDate = new Date(conversation.created_at).toLocaleDateString().toLowerCase();
    const updatedDate = new Date(conversation.updated_at).toLocaleDateString().toLowerCase();

    return (
      chatbotName.includes(searchLower) ||
      conversationId.includes(searchLower) ||
      messageCount.includes(searchLower) ||
      createdDate.includes(searchLower) ||
      updatedDate.includes(searchLower)
    );
  });

  if (conversationsLoading || chatbotsLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading activity data...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Activity Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <MessageSquare className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{activityStats.totalConversations}</p>
                <p className="text-sm text-gray-600">Conversations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Bot className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{activityStats.totalChatbots}</p>
                <p className="text-sm text-gray-600">Chatbots</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{activityStats.activeChatbots}</p>
                <p className="text-sm text-gray-600">Active Bots</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Activity className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">{activityStats.totalMessages}</p>
                <p className="text-sm text-gray-600">Messages</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Details */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Recent Activity</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="pingpost">PingPost</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Recent Activity Timeline</span>
              </CardTitle>
              <CardDescription>Latest user actions and events</CardDescription>
            </CardHeader>
            <CardContent>
              {recentActivity.length === 0 ? (
                <div className="text-center py-10">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No recent activity found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className={`flex items-start space-x-4 p-4 border rounded-lg ${
                        activity.type === 'conversation' ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''
                      }`}
                      onClick={() => activity.type === 'conversation' && handleViewConversation(activity.id)}
                    >
                      <div className="flex-shrink-0">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="text-sm font-medium">{activity.title}</p>
                          {getActivityBadge(activity.type)}
                          {activity.type === 'conversation' && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <Eye className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatRelativeTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="conversations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Conversations</CardTitle>
              <CardDescription>User's conversation history</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search Input */}
              <div className="flex items-center space-x-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by chatbot, ID, messages, or date..."
                    value={conversationSearchTerm}
                    onChange={(e) => setConversationSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {conversationSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConversationSearchTerm('')}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  Showing {filteredConversations.length} of {conversations.length} conversations
                </div>
              </div>

              {conversations.length === 0 ? (
                <div className="text-center py-10">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No conversations found</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-10">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No conversations match your search</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try adjusting your search terms or clear the search to see all conversations
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Chatbot</TableHead>
                        <TableHead>Messages</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Last Activity</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredConversations.map((conversation) => (
                        <TableRow
                          key={conversation.id}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleViewConversation(conversation.id)}
                        >
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Bot className="h-4 w-4 text-blue-600" />
                              <span>{conversation.chatbot?.name || 'Unknown Bot'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {conversation.message_count || 0} messages
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(conversation.created_at)}</TableCell>
                          <TableCell>{formatDate(conversation.updated_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewConversation(conversation.id);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pingpost" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Link2 className="h-5 w-5" />
                <span>PingPost Integration</span>
              </CardTitle>
              <CardDescription>User's PingPost providers and webhook endpoints</CardDescription>
            </CardHeader>
            <CardContent>
              <GlobalIntegrations
                  providedUser={{id: userId}}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Conversation Detail Modal */}
      <ConversationDetailModal
        conversationId={selectedConversationId}
        isOpen={isConversationModalOpen}
        onClose={handleCloseConversationModal}
      />
    </div>
  );
};
